// src/components/PWAInstallPrompt.tsx
import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';

export const PWAInstallPrompt: React.FC = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(isIOSDevice);

    // Check if already installed
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches ||
                       (window.navigator as any).standalone ||
                       document.referrer.includes('android-app://');

    if (isInstalled) {
      return;
    }

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt after 30 seconds or on 3rd visit
      const visitCount = parseInt(localStorage.getItem('pwa-visit-count') || '0');
      localStorage.setItem('pwa-visit-count', (visitCount + 1).toString());
      
      if (visitCount >= 2) {
        setTimeout(() => setShowPrompt(true), 30000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Show iOS prompt if needed
    if (isIOSDevice && !isInstalled) {
      const lastPrompt = localStorage.getItem('pwa-ios-prompt');
      if (!lastPrompt || Date.now() - parseInt(lastPrompt) > 7 * 24 * 60 * 60 * 1000) {
        setTimeout(() => setShowPrompt(true), 60000); // Show after 1 minute
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('PWA installed');
      }
      
      setDeferredPrompt(null);
      setShowPrompt(false);
    } else if (isIOS) {
      localStorage.setItem('pwa-ios-prompt', Date.now().toString());
      // Don't close for iOS, user needs to see instructions
    }
  };

  const handleClose = () => {
    setShowPrompt(false);
    if (isIOS) {
      localStorage.setItem('pwa-ios-prompt', Date.now().toString());
    }
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-in slide-in-from-bottom-5 duration-500">
      <div className="bg-gray-900/95 backdrop-blur-xl rounded-2xl border border-gray-800 shadow-2xl p-6">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-start gap-4">
          <div className="p-3 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl">
            <Smartphone className="w-6 h-6 text-white" />
          </div>
          
          <div className="flex-1">
            <h3 className="text-white font-semibold text-lg mb-2">
              Zainstaluj PlexiSystem
            </h3>
            
            {isIOS ? (
              <div className="text-gray-300 text-sm space-y-2">
                <p>Aby zainstalować aplikację na iOS:</p>
                <ol className="list-decimal list-inside space-y-1 text-xs">
                  <li>Naciśnij przycisk <span className="font-semibold">Udostępnij</span> na dole</li>
                  <li>Przewiń w dół i wybierz <span className="font-semibold">"Dodaj do ekranu głównego"</span></li>
                  <li>Naciśnij <span className="font-semibold">Dodaj</span></li>
                </ol>
              </div>
            ) : (
              <p className="text-gray-300 text-sm mb-4">
                Zainstaluj aplikację na swoim urządzeniu dla szybszego dostępu i pracy offline.
              </p>
            )}

            {!isIOS && (
              <div className="flex gap-3 mt-4">
                <button
                  onClick={handleInstall}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Zainstaluj
                </button>
                <button
                  onClick={handleClose}
                  className="px-4 py-2 bg-gray-800 text-gray-300 rounded-xl hover:bg-gray-700 transition-all"
                >
                  Później
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Benefits */}
        <div className="mt-4 pt-4 border-t border-gray-800">
          <div className="flex items-center gap-6 text-xs text-gray-400">
            <span className="flex items-center gap-1">
              ✓ Działa offline
            </span>
            <span className="flex items-center gap-1">
              ✓ Szybszy dostęp
            </span>
            <span className="flex items-center gap-1">
              ✓ Powiadomienia
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
